import React from "react";

import { Control } from "react-hook-form";

import Validator from "../../../utils/validation/Validator";
import ReactHookInput from "../forms/ReactHookInput";

export type Lang = "de" | "en" | "pl";

export interface ContactBaseFieldsTranslations {
    name: string;
    email: string;
    phone: string;
    messageErrorEmail: string;
    messageErrorPhone: string;
}

interface ContactBaseFieldValues {
    name: string;
    email: string;
    phone: string;
}

interface BaseInputsProps<TFieldValues extends ContactBaseFieldValues> {
    control: Control<TFieldValues>;
    translations: ContactBaseFieldsTranslations;
    /** Some forms treat phone as optional; defaults to required. */
    phoneRequired?: boolean;
}

const BaseInputs = <TFieldValues extends ContactBaseFieldValues>({
    control,
    translations,
    phoneRequired = true,
}: BaseInputsProps<TFieldValues>) => {
    // ReactHookInput is itself generic over the field-values shape; TS can't
    // resolve its Path<fvs> conditional type against an abstract TFieldValues,
    // so we narrow to the concrete shape we actually rely on here.
    const baseControl = control as unknown as Control<ContactBaseFieldValues>;

    return (
        <>
            <ReactHookInput
                name="name"
                fullWidth
                placeholder={translations.name}
                rules={{
                    required: true,
                    maxLength: 50,
                }}
                control={baseControl}
            />
            <ReactHookInput
                name="email"
                fullWidth
                placeholder={translations.email}
                rules={{
                    required: true,
                    maxLength: 50,
                    validate: (value: string) =>
                        new Validator().isValidEmail(value) ||
                        translations.messageErrorEmail,
                }}
                control={baseControl}
            />
            <ReactHookInput
                name="phone"
                fullWidth
                placeholder={translations.phone}
                rules={{
                    required: phoneRequired,
                    maxLength: 20,
                    validate: (value: string) =>
                        (!phoneRequired && value === "") ||
                        new Validator().isValidPhone(value) ||
                        translations.messageErrorPhone,
                }}
                control={baseControl}
            />
        </>
    );
};

export default BaseInputs;
